import React, { useEffect, useCallback } from "react";
import { Table, Button, Tag, Alert, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import debounce from "lodash.debounce";
import { fetchAllQuotations } from "../../../../../Store/Slices/Finance/Quotations/quotationThunks";
import Spinner from "../../../../../Components/Common/Spinner";
import ProtectedSection from "../../../../../Routes/ProtectedRoutes/ProtectedSection";
import { PERMISSIONS } from "../../../../../config/permission";
import ProtectedAction from "../../../../../Routes/ProtectedRoutes/ProtectedAction";
import { useTranslation } from "react-i18next";
import TableShimmer from "../../../../../Components/Common/TableShimmer";

const RecentQuotation = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const schoolCurrency = useSelector(
    (store) => store.common.user.userDetails?.currency,
  );
  // Extracting necessary state from Redux store
  const { quotations, totalRecords, loading, error } = useSelector(
    (state) => state.admin.quotations,
  );
  const { t } = useTranslation("prtFinance");
  // Debounced function to fetch quotations with a fixed limit of 5
  const debouncedFetch = useCallback(
    debounce((params) => {
      dispatch(fetchAllQuotations(params));
    }, 300),
    [dispatch],
  );

  // Fetch data on component mount with limit set to 5
  useEffect(() => {
    const params = {
      page: 1, // Always fetch the first page
      limit: 5, // Limit to 5 records
    };
    debouncedFetch(params);
  }, [debouncedFetch]);

  // Handle "View More" button click
  const handleViewMore = () => {
    navigate("/finance/quotations/quotations-list");
  };

  // Define table columns with fixed widths and ellipsis
  const columns = [
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          {t("Quotation Number")}
        </span>
      ),
      dataIndex: "quotationNumber",
      key: "quotationNumber",
      render: (text) => (
        <span className="inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wider bg-purple-50 text-purple-600 border border-purple-100">
          {text}
        </span>
      ),
      width: 120,
      ellipsis: true,
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          {t("Quotation To")}
        </span>
      ),
      dataIndex: "quotationTo",
      key: "quotationTo",
      render: (text) => (
        <span className="text-sm font-semibold text-gray-800">{text}</span>
      ),
      width: 150,
      ellipsis: true,
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          {t("Purpose")}
        </span>
      ),
      dataIndex: "purpose",
      key: "purpose",
      render: (text) => <span className="text-sm text-gray-600">{text}</span>,
      width: 120,
      ellipsis: true,
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          {t("Discount")}
        </span>
      ),
      dataIndex: "discount",
      key: "discount",
      render: (value, record) =>
        record.discountType === "percentage" ? (
          <span className="inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wider bg-purple-50 text-purple-600 border border-purple-100">
            {value || 0}%
          </span>
        ) : (
          <span className="inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wider bg-orange-50 text-orange-500 border border-orange-200">
            {value || 0} {schoolCurrency}
          </span>
        ),
      width: 100,
      ellipsis: true,
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          {`${t("Total Amount")} ${schoolCurrency}`}
        </span>
      ),
      dataIndex: "total_amount",
      key: "total_amount",
      sorter: (a, b) => (a.total_amount || 0) - (b.total_amount || 0),
      render: (value) => (
        <span className="text-sm font-semibold text-gray-700">
          {value || "0"} {schoolCurrency}
        </span>
      ),
      width: 120,
      ellipsis: true,
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          {`${t("Final Amount")} ${schoolCurrency}`}
        </span>
      ),
      dataIndex: "final_amount",
      key: "final_amount",
      sorter: (a, b) => (a.final_amount || 0) - (b.final_amount || 0),
      render: (value) => (
        <span className="text-sm font-semibold text-purple-600">
          {value || "0"} {schoolCurrency}
        </span>
      ),
      width: 120,
      ellipsis: true,
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          {t("Status")}
        </span>
      ),
      dataIndex: "status",
      key: "status",
      render: (status, record) => {
        let displayStatus = status;
        let color = "default";
        if (record.isCancel) {
          displayStatus = "reject";
          color = "red";
        } else {
          switch (status) {
            case "accept":
              color = "green";
              break;
            case "pending":
              color = "blue";
              break;
            case "reject":
              color = "red";
              break;
            default:
              color = "default";
          }
        }
        const map = {
          green: "bg-green-50 text-green-600 border-green-200",
          blue: "bg-blue-50 text-blue-600 border-blue-100",
          red: "bg-red-50 text-red-500 border-red-100",
          default: "bg-gray-50 text-gray-500 border-gray-100",
        };
        const dot = {
          green: "bg-green-500",
          blue: "bg-blue-500",
          red: "bg-red-400",
          default: "bg-gray-400",
        };
        return (
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold capitalize tracking-wider border ${map[color]}`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dot[color]}`}
            />
            {displayStatus || t("N/A")}
          </span>
        );
      },
      width: 80,
      ellipsis: true,
    },
  ];

  // Transform quotations data to table dataSource
  const dataSource = quotations?.slice(0, 5).map((quotation) => ({
    key: quotation._id,
    quotationNumber: quotation.quotationNumber || t("N/A"),
    quotationTo: quotation.receiver?.name || t("N/A"),
    purpose: quotation.purpose || t("N/A"),
    discount: quotation.discount || 0,
    discountType: quotation.discountType || t("percentage"),
    final_amount: quotation.final_amount || 0,
    total_amount: quotation.total_amount || 0,
    status: quotation.status || t("N/A"),
    isCancel: quotation.isCancel,
  }));

  // Helper function to render content based on state
  const renderContent = () => {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <Table
          dataSource={dataSource}
          columns={columns}
          pagination={false} // Removed pagination controls
          className="rounded-xl  text-xs"
          size="small"
          tableLayout="fixed"
          components={{
              body: {
                row: (props) => <tr {...props} style={{ height: "60px" }} />,
              },
            }}
          // Fixed table layout
        />
      </div>
    );
  };

  return (
    <div className="  rounded-xl  space-y-4 mt-6">
      {/* Header */}
      <div className="flex justify-between items-center bg-white rounded-xl shadow-sm p-4">
        <h2 className="staff-heading mb-4">
          {t("Summary of Quotation")} ({dataSource?.length || 0}/{totalRecords})
        </h2>
        <button
          onClick={handleViewMore}
          className="btn-gradient px-4 py-2 btn-gradient"
          size="small"
        >
          {t("View More")} ({totalRecords})
        </button>
      </div>

      {loading ? (
        <div className="mx-5">
          <TableShimmer columns={7} />{" "}
        </div>
      ) : (
        renderContent()
      )}
    </div>
  );
};

export default RecentQuotation;
